import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Badge,
  Code,
  Link,
} from "@chakra-ui/react";
import {
  InfoOutlineIcon,
  QuestionOutlineIcon,
} from "@chakra-ui/icons";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from "@chakra-ui/react";
import AppLayout from "../components/layout/AppLayout";
import PageHeader from "../components/PageHeader";

const PricingTools = () => {
  const toast = useToast();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // inject /api only if it's not already present on API_BASE
  const buildUrl = (p) => {
    const base = (API_BASE || "").replace(/\/$/, "");
    return /\/api$/.test(base) ? `${base}${p}` : `${base}/api${p}`;
  };

  // ===== auth header helper =====
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // ====== Everyday SRP ======
  const [srpInputs, setSrpInputs] = useState({
    cogs: "",
    distributor_margin_pct: "",
    retailer_margin_pct: "",
    desired_brand_margin_pct: "",
    target_srp: "",
    margin_basis: "margin",
  });
  const [srpResult, setSrpResult] = useState(null);

  // ====== Promo ROI ======
  const [promoInputs, setPromoInputs] = useState({
    perspective: "brand",
    base_units: "",
    base_price: "",
    promo_price: "",
    discount_pct: "",
    lift_pct: "",
    promo_units: "",
    unit_cost: "",
    trade_spend_total: "0",
  });
  const [promoResult, setPromoResult] = useState(null);

  // ====== Break-even ======
  const [beInputs, setBeInputs] = useState({
    fixed_cost: "",
    unit_margin: "",
  });
  const [beResult, setBeResult] = useState(null);

  // ====== Velocity Forecast ======
  const [vfInputs, setVfInputs] = useState({
    stores: "",
    acv_pct: "",
    total_stores: "",
    velocity_units_per_store_per_week: "",
    weeks: "",
    srp: "",
    brand_sell_in: "",
    unit_cost: "",
  });
  const [vfResult, setVfResult] = useState(null);

  const asNum = (v) =>
    v === "" || v === null || v === undefined ? undefined : Number(v);

  const postJson = async (path, body) => {
    const res = await fetch(buildUrl(path), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data?.detail || data?.error) {
      const msg = data?.detail || data?.error || "Request failed";
      throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
    return data;
  };

  // -------- Handlers
  const handleEverydaySRP = async () => {
    try {
      const payload = {
        cogs: asNum(srpInputs.cogs),
        distributor_margin_pct: asNum(srpInputs.distributor_margin_pct),
        retailer_margin_pct: asNum(srpInputs.retailer_margin_pct),
        desired_brand_margin_pct: srpInputs.target_srp
          ? undefined
          : asNum(srpInputs.desired_brand_margin_pct),
        target_srp: srpInputs.target_srp
          ? asNum(srpInputs.target_srp)
          : undefined,
        margin_basis: srpInputs.margin_basis,
      };
      const data = await postJson(`/pricing/everyday-srp/`, payload);
      setSrpResult(data);
      toast({
        title: "Calculated Everyday SRP",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Everyday SRP Error",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handlePromoROI = async () => {
    try {
      const payload = {
        perspective: promoInputs.perspective,
        base_units: promoInputs.base_units
          ? asNum(promoInputs.base_units)
          : undefined,
        base_price: asNum(promoInputs.base_price),
        promo_price: promoInputs.promo_price
          ? asNum(promoInputs.promo_price)
          : undefined,
        discount_pct: promoInputs.discount_pct
          ? asNum(promoInputs.discount_pct)
          : undefined,
        lift_pct: promoInputs.lift_pct
          ? asNum(promoInputs.lift_pct)
          : undefined,
        promo_units: promoInputs.promo_units
          ? asNum(promoInputs.promo_units)
          : undefined,
        unit_cost: promoInputs.unit_cost
          ? asNum(promoInputs.unit_cost)
          : undefined,
        trade_spend_total: asNum(promoInputs.trade_spend_total) ?? 0,
      };
      const data = await postJson(`/pricing/promo-roi/`, payload);
      setPromoResult(data);
      toast({
        title: "Calculated Promo ROI",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Promo ROI Error",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleBreakEven = async () => {
    try {
      const payload = {
        fixed_cost: asNum(beInputs.fixed_cost),
        unit_margin: asNum(beInputs.unit_margin),
      };
      const data = await postJson(`/pricing/breakeven-volume/`, payload);
      setBeResult(data);
      toast({
        title: "Calculated Break-Even",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Break-Even Error",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleVelocityForecast = async () => {
    try {
      const payload = {
        stores: vfInputs.stores ? asNum(vfInputs.stores) : undefined,
        acv_pct: vfInputs.acv_pct ? asNum(vfInputs.acv_pct) : undefined,
        total_stores: vfInputs.total_stores
          ? asNum(vfInputs.total_stores)
          : undefined,
        velocity_units_per_store_per_week: asNum(
          vfInputs.velocity_units_per_store_per_week
        ),
        weeks: asNum(vfInputs.weeks),
        srp: vfInputs.srp ? asNum(vfInputs.srp) : undefined,
        brand_sell_in: vfInputs.brand_sell_in
          ? asNum(vfInputs.brand_sell_in)
          : undefined,
        unit_cost: vfInputs.unit_cost ? asNum(vfInputs.unit_cost) : undefined,
      };
      const data = await postJson(`/pricing/velocity-forecast/`, payload);
      setVfResult(data);
      toast({
        title: "Calculated Velocity Forecast",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Velocity Forecast Error",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // -------- Reusable number field
  // Supports decimals by default; set integerOnly to enforce whole numbers
  // Allows thousands separators while typing; normalizes on blur.
  const NumberField = ({
    label,
    value,
    onChange,
    placeholder,
    suffix,
    integerOnly = false,
  }) => {
    const [local, setLocal] = useState(value ?? "");

    useEffect(() => {
      setLocal(value ?? "");
    }, [value]);

    const commit = () => {
      if (local === "") {
        onChange("");
        return;
      }
      // Remove thousands commas and normalize decimal commas to dots
      let cleaned = local.replace(/,/g, "");
      if (!integerOnly) {
        cleaned = cleaned.replace(/\s/g, "").replace(/,/g, "");
      }
      // If user used comma as decimal, convert last comma to dot (typed formats)
      if (!integerOnly && /,/.test(local) && !/\./.test(local)) {
        cleaned = local.replace(/\./g, "").replace(/,/g, ".");
      }
      // Final strip thousands and keep decimal for non-integers
      cleaned = cleaned.replace(/,/g, "");
      if (integerOnly) {
        cleaned = cleaned.replace(/\D/g, "");
      }
      onChange(cleaned);
    };

    const regex = integerOnly
      ? /^[0-9,]*$/ // allow digits and commas while typing
      : /^[0-9]*([.,][0-9]*)?$/; // digits with one decimal (.,)

    return (
      <FormControl>
        <FormLabel>{label}</FormLabel>
        <Input
          value={local}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "" || regex.test(v)) {
              setLocal(v);
            }
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commit();
              e.currentTarget.blur();
            }
          }}
          placeholder={placeholder || ""}
          inputMode={integerOnly ? "numeric" : "decimal"}
          pattern={integerOnly ? "[0-9,]*" : "[0-9.,]*"}
          bg="white"
        />
        {suffix && (
          <Text fontSize="xs" color="gray.500" mt={1}>
            {suffix}
          </Text>
        )}
      </FormControl>
    );
  };

  // ===== Formatting helpers + result card =====
  const fmtMoney = (n) =>
    n == null || Number.isNaN(n)
      ? "—"
      : new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }).format(n);

  const fmtPct = (n) =>
    n == null || Number.isNaN(n) ? "—" : `${(Number(n) * 100).toFixed(1)}%`;

  const fmtNumber = (n) =>
    n == null || Number.isNaN(n) ? "—" : new Intl.NumberFormat().format(n);

  const ResultCard = ({ title, data, kind = "generic" }) => {
    if (!data) return null;

    const fieldMaps = {
      srp: [
        ["brand_sell_in", "Brand Sell-In", fmtMoney],
        ["distributor_sell_in", "Distributor Sell-In", fmtMoney],
        ["srp", "SRP", fmtMoney],
        ["brand_margin_pct", "Brand Margin %", fmtPct],
        ["distributor_margin_pct", "Distributor Margin %", fmtPct],
        ["retailer_margin_pct", "Retailer Margin %", fmtPct],
      ],
      promo: [
        ["base_revenue", "Base Revenue", fmtMoney],
        ["promo_revenue", "Promo Revenue", fmtMoney],
        ["incremental_revenue", "Incremental Revenue", fmtMoney],
        ["base_profit", "Base Profit", fmtMoney],
        ["promo_profit", "Promo Profit", fmtMoney],
        ["incremental_profit", "Incremental Profit", fmtMoney],
        ["promo_units", "Promo Units", fmtNumber],
        ["incremental_units", "Incremental Units", fmtNumber],
        ["breakeven_discount_pct", "Breakeven Discount", fmtPct],
      ],
      be: [
        ["units_to_breakeven_exact", "Units to Breakeven (exact)", fmtNumber],
        ["units_to_breakeven_ceiling", "Units to Breakeven (ceiling)", fmtNumber],
      ],
      velocity: [
        ["stores", "Stores", fmtNumber],
        ["total_units", "Total Units", fmtNumber],
        ["revenue_srp", "Revenue (SRP)", fmtMoney],
        ["revenue_brand", "Revenue (Brand)", fmtMoney],
        ["profit_brand", "Profit (Brand)", fmtMoney],
      ],
      generic: Object.keys(data || {}).map((k) => [k, k, (v) => String(v)]),
    };

    const rows = (fieldMaps[kind] || fieldMaps.generic).filter(
      ([key]) => key in data
    );

    return (
      <Box
        mt={4}
        p={4}
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold">{title}</Text>
          <Badge colorScheme="green">RESULT</Badge>
        </HStack>

        <SimpleGrid columns={[1, 2, 3]} spacing={4}>
          {rows.map(([key, label, fmt]) => (
            <Box
              key={key}
              p={3}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Text fontSize="sm" color="gray.600">
                {label}
              </Text>
              <Text fontWeight="semibold" mt={1}>
                {fmt(data[key])}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  };

  // -------- “When to use” strip (green, concise)
  const UseCaseStrip = ({ children, learnMore }) => (
    <Box
      mb={4}
      p={3}
      bg="green.50"
      border="1px solid"
      borderColor="green.100"
      borderRadius="md"
    >
      <HStack align="start" spacing={3}>
        <InfoOutlineIcon color="green.600" mt="2px" />
        <Box>
          <HStack spacing={2} mb={1}>
            <Text fontWeight="semibold" color="green.800">
              When to use
            </Text>
            {learnMore && (
              <Popover placement="bottom-start">
                <PopoverTrigger>
                  <HStack
                    as={Link}
                    color="green.700"
                    fontSize="sm"
                    cursor="pointer"
                  >
                    <QuestionOutlineIcon />
                    <Text>Learn more</Text>
                  </HStack>
                </PopoverTrigger>
                <PopoverContent w="sm">
                  <PopoverArrow />
                  <PopoverBody fontSize="sm">{learnMore}</PopoverBody>
                </PopoverContent>
              </Popover>
            )}
          </HStack>
          <Text fontSize="sm" color="gray.800">
            {children}
          </Text>
        </Box>
      </HStack>
    </Box>
  );

  return (
    <AppLayout>
      <VStack
        spacing={6}
        maxW="980px"
        mx="auto"
        mt={10}
        align="stretch"
        overflowY="visible"
      >
        <PageHeader
          title="Pricing Tools"
          subtitle="Quick calculators for SRP/margins, promo ROI, break-even units, and
          velocity-based revenue."
        />

        <Tabs variant="enclosed">
          <TabList>
            <Tab
              borderRadius="md"
              fontWeight="semibold"
              _selected={{
                bg: "green.500",
                color: "white",
                borderColor: "green.500",
              }}
            >
              Everyday SRP
            </Tab>
            <Tab
              borderRadius="md"
              fontWeight="semibold"
              _selected={{
                bg: "green.500",
                color: "white",
                borderColor: "green.500",
              }}
            >
              Promo ROI
            </Tab>
            <Tab
              borderRadius="md"
              fontWeight="semibold"
              _selected={{
                bg: "green.500",
                color: "white",
                borderColor: "green.500",
              }}
            >
              Break-Even Volume
            </Tab>
            <Tab
              borderRadius="md"
              fontWeight="semibold"
              _selected={{
                bg: "green.500",
                color: "white",
                borderColor: "green.500",
              }}
            >
              Velocity → Revenue
            </Tab>
          </TabList>

          <TabPanels>
            {/* Everyday SRP */}
            <TabPanel>
              <UseCaseStrip
                learnMore={
                  <>
                    • Set/validate list price that supports your COGS & channel
                    margins. <br />
                    • Compare scenarios: retailer asks for +5 pts, or switch
                    margin basis (markup vs margin).
                  </>
                }
              >
                Back into SRP and channel sell-ins that hit brand/distributor/retailer
                margin targets.
              </UseCaseStrip>

              <SimpleGrid columns={[1, 2]} spacing={4}>
                <NumberField
                  label="COGS (per unit)"
                  value={srpInputs.cogs}
                  onChange={(v) =>
                    setSrpInputs((s) => ({ ...s, cogs: v }))
                  }
                />
                <NumberField
                  label="Distributor Margin %"
                  value={srpInputs.distributor_margin_pct}
                  onChange={(v) =>
                    setSrpInputs((s) => ({
                      ...s,
                      distributor_margin_pct: v,
                    }))
                  }
                  suffix="e.g., 15 or 0.15"
                />
                <NumberField
                  label="Retailer Margin %"
                  value={srpInputs.retailer_margin_pct}
                  onChange={(v) =>
                    setSrpInputs((s) => ({
                      ...s,
                      retailer_margin_pct: v,
                    }))
                  }
                  suffix="e.g., 35 or 0.35"
                />
                <FormControl>
                  <FormLabel>Margin Basis</FormLabel>
                  <Select
                    value={srpInputs.margin_basis}
                    onChange={(e) =>
                      setSrpInputs((s) => ({
                        ...s,
                        margin_basis: e.target.value,
                      }))
                    }
                    bg="white"
                  >
                    <option value="margin">Margin on Price (default)</option>
                    <option value="markup">Markup on Cost</option>
                  </Select>
                </FormControl>
                <NumberField
                  label="Desired Brand Margin %"
                  value={srpInputs.desired_brand_margin_pct}
                  onChange={(v) =>
                    setSrpInputs((s) => ({
                      ...s,
                      desired_brand_margin_pct: v,
                      target_srp: "",
                    }))
                  }
                  suffix="Use this OR Target SRP"
                />
                <NumberField
                  label="Target SRP"
                  value={srpInputs.target_srp}
                  onChange={(v) =>
                    setSrpInputs((s) => ({
                      ...s,
                      target_srp: v,
                      desired_brand_margin_pct: "",
                    }))
                  }
                />
              </SimpleGrid>
              <HStack mt={4}>
                <Button colorScheme="green" onClick={handleEverydaySRP}>
                  Calculate
                </Button>
              </HStack>
              {srpResult && (
                <ResultCard
                  title="Everyday SRP / Margin"
                  data={srpResult}
                  kind="srp"
                />
              )}
            </TabPanel>

            {/* Promo ROI */}
            <TabPanel>
              <UseCaseStrip
                learnMore={
                  <>
                    • Test breakeven discount and ROI before approving trade.
                    <br />• Provide either <em>Promo Price</em> or{" "}
                    <em>Discount%</em>; and either <em>Lift%</em> or{" "}
                    <em>Promo Units</em>.
                  </>
                }
              >
                Estimate units, revenue, profit and breakeven discount for a
                single promotion.
              </UseCaseStrip>

              <SimpleGrid columns={[1, 2]} spacing={4}>
                <FormControl>
                  <FormLabel>Perspective</FormLabel>
                  <Select
                    value={promoInputs.perspective}
                    onChange={(e) =>
                      setPromoInputs((s) => ({
                        ...s,
                        perspective: e.target.value,
                      }))
                    }
                    bg="white"
                  >
                    <option value="brand">Brand (sell-in)</option>
                    <option value="retailer">Retailer (SRP)</option>
                  </Select>
                </FormControl>
                <NumberField
                  label="Base Units"
                  value={promoInputs.base_units}
                  onChange={(v) =>
                    setPromoInputs((s) => ({ ...s, base_units: v }))
                  }
                  integerOnly
                />
                <NumberField
                  label="Base Price"
                  value={promoInputs.base_price}
                  onChange={(v) =>
                    setPromoInputs((s) => ({ ...s, base_price: v }))
                  }
                />
                <NumberField
                  label="Promo Price (optional)"
                  value={promoInputs.promo_price}
                  onChange={(v) =>
                    setPromoInputs((s) => ({
                      ...s,
                      promo_price: v,
                      discount_pct: "",
                    }))
                  }
                />
                <NumberField
                  label="Discount % (optional)"
                  value={promoInputs.discount_pct}
                  onChange={(v) =>
                    setPromoInputs((s) => ({
                      ...s,
                      discount_pct: v,
                      promo_price: "",
                    }))
                  }
                  suffix="Use price OR discount"
                />
                <NumberField
                  label="Lift % (or provide Promo Units)"
                  value={promoInputs.lift_pct}
                  onChange={(v) =>
                    setPromoInputs((s) => ({
                      ...s,
                      lift_pct: v,
                      promo_units: "",
                    }))
                  }
                  suffix="Use lift OR promo units"
                />
                <NumberField
                  label="Promo Units (or provide Lift %)"
                  value={promoInputs.promo_units}
                  onChange={(v) =>
                    setPromoInputs((s) => ({
                      ...s,
                      promo_units: v,
                      lift_pct: "",
                    }))
                  }
                  integerOnly
                />
                <NumberField
                  label="Unit Cost"
                  value={promoInputs.unit_cost}
                  onChange={(v) =>
                    setPromoInputs((s) => ({ ...s, unit_cost: v }))
                  }
                />
                <NumberField
                  label="Trade Spend Total"
                  value={promoInputs.trade_spend_total}
                  onChange={(v) =>
                    setPromoInputs((s) => ({ ...s, trade_spend_total: v }))
                  }
                />
              </SimpleGrid>
              <HStack mt={4}>
                <Button colorScheme="green" onClick={handlePromoROI}>
                  Calculate
                </Button>
              </HStack>
              {promoResult && (
                <ResultCard title="Promo ROI" data={promoResult} kind="promo" />
              )}
            </TabPanel>

            {/* Break-even */}
            <TabPanel>
              <UseCaseStrip
                learnMore={
                  <>
                    • Slotting, MCB, demo budget, paid search test, freight min
                    — any fixed spend. <br />• Enter the fixed cost and per-unit
                    margin to get exact and rounded units needed.
                  </>
                }
              >
                Find how many units recover a fixed cost at your current
                per-unit margin.
              </UseCaseStrip>

              <SimpleGrid columns={[1, 2]} spacing={4}>
                <NumberField
                  label="Fixed Cost"
                  value={beInputs.fixed_cost}
                  onChange={(v) =>
                    setBeInputs((s) => ({ ...s, fixed_cost: v }))
                  }
                />
                <NumberField
                  label="Unit Margin"
                  value={beInputs.unit_margin}
                  onChange={(v) =>
                    setBeInputs((s) => ({ ...s, unit_margin: v }))
                  }
                />
              </SimpleGrid>
              <HStack mt={4}>
                <Button colorScheme="green" onClick={handleBreakEven}>
                  Calculate
                </Button>
              </HStack>
              {beResult && (
                <ResultCard
                  title="Break-Even Volume"
                  data={beResult}
                  kind="be"
                />
              )}
            </TabPanel>

            {/* Velocity → Revenue */}
            <TabPanel>
              <UseCaseStrip
                learnMore={
                  <>
                    • Provide <em>Stores</em> or <em>ACV% + Total Stores</em>,
                    plus velocity & weeks. <br />• Add SRP / Sell-In / Cost to
                    see revenue & profit views.
                  </>
                }
              >
                Turn velocity into total units, SRP revenue, brand revenue, and
                brand profit.
              </UseCaseStrip>

              <SimpleGrid columns={[1, 2]} spacing={4}>
                <NumberField
                  label="Stores"
                  value={vfInputs.stores}
                  onChange={(v) =>
                    setVfInputs((s) => ({ ...s, stores: v }))
                  }
                  integerOnly
                />
                <NumberField
                  label="ACV % (optional)"
                  value={vfInputs.acv_pct}
                  onChange={(v) =>
                    setVfInputs((s) => ({ ...s, acv_pct: v }))
                  }
                  suffix="Provide with total stores"
                />
                <NumberField
                  label="Total Stores (if using ACV%)"
                  value={vfInputs.total_stores}
                  onChange={(v) =>
                    setVfInputs((s) => ({ ...s, total_stores: v }))
                  }
                  integerOnly
                />
                <NumberField
                  label="Velocity (units/store/week)"
                  value={vfInputs.velocity_units_per_store_per_week}
                  onChange={(v) =>
                    setVfInputs((s) => ({
                      ...s,
                      velocity_units_per_store_per_week: v,
                    }))
                  }
                />
                <NumberField
                  label="Weeks"
                  value={vfInputs.weeks}
                  onChange={(v) =>
                    setVfInputs((s) => ({ ...s, weeks: v }))
                  }
                  integerOnly
                />
                <NumberField
                  label="SRP"
                  value={vfInputs.srp}
                  onChange={(v) =>
                    setVfInputs((s) => ({ ...s, srp: v }))
                  }
                />
                <NumberField
                  label="Brand Sell-In"
                  value={vfInputs.brand_sell_in}
                  onChange={(v) =>
                    setVfInputs((s) => ({ ...s, brand_sell_in: v }))
                  }
                />
                <NumberField
                  label="Unit Cost"
                  value={vfInputs.unit_cost}
                  onChange={(v) =>
                    setVfInputs((s) => ({ ...s, unit_cost: v }))
                  }
                />
              </SimpleGrid>
              <HStack mt={4}>
                <Button colorScheme="green" onClick={handleVelocityForecast}>
                  Calculate
                </Button>
              </HStack>
              {vfResult && (
                <ResultCard
                  title="Velocity → Revenue Forecast"
                  data={vfResult}
                  kind="velocity"
                />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </AppLayout>
  );
};

export default PricingTools;
